import JSZip from "jszip";
import { v4 as uuid } from 'uuid';

import { PluginContextType } from "../plugin/types";
import { bibleDB } from "./database";
import { APIVersion, Verse, VerseReference } from "./types";
import { API_ROOT_URL, decodeContent, extractVerseData } from "./youversion";
import { LineType, ResourceType, ScriptureBlockType, ScriptureResource } from "../resources/types";

function base64ToArrayBuffer(base64: string) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export const beginDownload = async (pluginContext: PluginContextType, version: APIVersion) => {
  await bibleDB.versions.add({
    id: version.id,
    title: version.local_title,
    abbreviation: version.local_abbreviation,
    language: version.language.iso_639_1,
    download_state: 'pending',
    source_url: version.offline.url
  });

  const versionWithBooks: APIVersion = await pluginContext.fetch(
    `${API_ROOT_URL}/version/3.1?id=${version.id}`,
    { json: true }
  ) as APIVersion;

  if (versionWithBooks.copyright_short) {
    await bibleDB.versions.update(version.id, { copyright: versionWithBooks.copyright_short.text });
  }

  for (const [index, book] of versionWithBooks.books!.entries()) {
    await bibleDB.books.add({
      version_id: version.id,
      id: book.usfm,
      index,
      title: book.human
    });
  }

  const response = await pluginContext.fetch(
    `https:${version.offline.url}`,
    { binary: true }
  );

  const buf = base64ToArrayBuffer(response as string);

  await bibleDB.versions.update(version.id, { download_state: 'extracting' });

  const zip = await JSZip.loadAsync(buf);
  const chapterFiles = zip.filter(path => !!path.match(/[A-Z0-9]+\/[0-9]+\.yves/));

  for (const file of chapterFiles) {
    const encodedChapter = await file.async('uint8array');
    const decoded = decodeContent(encodedChapter);
    const verseData = extractVerseData(decoded);

    const [bookID, chapterFilename] = file.name.split('/');
    const chapterIndex = parseInt(chapterFilename, 10);

    const chapter = {
      version_id: version.id,
      book_id: bookID,
      index: chapterIndex
    };

    await bibleDB.chapters.add(chapter);

    const book = await bibleDB.books.get({ version_id: version.id, id: bookID });

    const verses = Object.entries(verseData).map(
      ([usfm, text]) => ({
        version_id: version.id,
        book_index: book!.index,
        chapter_index: chapterIndex,
        index: parseInt(usfm.split('.')[2], 10),
        content: text
      })
    );

    await bibleDB.verses.bulkAdd(verses);
  }

  await bibleDB.versions.update(version.id, { download_state: 'complete' });
};

export const removeVersion = async (versionID: number) => {
  await bibleDB.verses.filter(c => c.version_id == versionID).delete();
  await bibleDB.chapters.filter(c => c.version_id == versionID).delete();
  await bibleDB.books.filter(b => b.version_id == versionID).delete();
  await bibleDB.versions.delete(versionID);
};

/**
 * Returns `true` iff `first` is equal to or before `second` in canonical order
 * for the given Bible `versionID`.
 */
export const areReferencesWellOrdered = async (versionID: number, first: VerseReference, second: VerseReference): Promise<boolean> => {
  const firstBook = await bibleDB.books.where({ '[version_id+id]': [versionID, first.book] }).first();
  const secondBook = await bibleDB.books.where({ '[version_id+id]': [versionID, second.book] }).first();

  if (!firstBook || !secondBook) {
    return false;
  }

  return (firstBook.index <= secondBook.index) &&
    (first.chapter <= second.chapter) &&
    (first.verse <= second.verse);
};

export const retrieveContent = async (versionID: number, startRef: VerseReference, endRef: VerseReference) => {
  const startBook = await bibleDB.books.where({ '[version_id+id]': [versionID, startRef.book] }).first();
  const endBook = await bibleDB.books.where({ '[version_id+id]': [versionID, endRef.book] }).first();

  // Handle nonsense cases (no book or endRef before startRef)
  if (!startBook || !endBook || startBook?.index > endBook?.index) {
    return [];
  }

  if (startBook.index === endBook.index) {
    if (startRef.chapter > endRef.chapter) {
      return [];
    }
    else if (startRef.chapter === endRef.chapter && startRef.verse > endRef.verse) {
      return [];
    }
  }

  const verses = await bibleDB.verses.where('[version_id+book_index+chapter_index+index]').between(
    [versionID, startBook.index, startRef.chapter, startRef.verse],
    [versionID, endBook.index, endRef.chapter, endRef.verse + 1],
  ).toArray();

  return verses;
};

const deriveAnnotation = (verses: Verse[], verse: Verse, index: number): string => {
  const showChapter = (index === 0) || (verses[index - 1].chapter_index !== verse.chapter_index);
  return `${ showChapter ? `${verse.chapter_index}:` : '' }${verse.index}`;
};

export const createResource = async (versionID: number, startRef: VerseReference, endRef: VerseReference): Promise<ScriptureResource> => {
  const bible = await bibleDB.versions.get(versionID);
  const startBook = await bibleDB.books.where({ '[version_id+id]': [versionID, startRef.book] }).first();
  const endBook = await bibleDB.books.where({ '[version_id+id]': [versionID, endRef.book] }).first();

  const verses = await retrieveContent(versionID, startRef, endRef);

  let title = `${startBook?.title} ${startRef.chapter}: ${startRef.verse} – `;
  if (startRef.book !== endRef.book) {
    title += `${endBook!.title} `;
  }
  if (startRef.chapter !== endRef.chapter) {
    title += `${endRef.chapter}: `;
  }
  title += `${endRef.verse}`;

  return {
    id: `SCR-${uuid()}`,
    title,
    type: ResourceType.SCRIPTURE,
    meta: {
      citation: title,
      copyright: bible?.copyright,
      version: bible!.abbreviation
    },
    content: [{
      type: ScriptureBlockType.SCRIPTURE,
      content: [
        verses.map(
          (verse, idx) => ([ verse.content, LineType.NORMAL, deriveAnnotation(verses, verse, idx)])
        )
      ]
    }]
  };
};
